// SalonFlow Console: sign-in (password + TOTP) and platform admin pages.
import { type FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Route, Routes, useNavigate, useParams } from 'react-router';
import { ApiError } from '../api/client.ts';
import { message } from '../lib/errors.ts';
import { Banner, Button, Card, Chip, Dialog, Empty, Field, Loading, useToast } from '../ui/index.tsx';
import type { CheckItem, ConsoleApi, ConsoleShop, ConsoleShopDetail, ErrorRow } from './api.ts';
import type { ConsoleAuth } from './auth.ts';
import { MenuDesigner } from '../owner/MenuDesigner.tsx';

type Gate = { kind: 'loading' } | { kind: 'login' } | { kind: 'mfa'; factorId: string | null } | { kind: 'not-admin'; email: string } | { kind: 'ready'; email: string };

export function ConsoleRoot({ auth, makeApi }: { auth: ConsoleAuth; makeApi: (getToken: () => Promise<string | null>) => ConsoleApi }) {
  const api = useMemo(() => makeApi(async () => (await auth.getSession())?.accessToken ?? null), [auth, makeApi]);
  const [gate, setGate] = useState<Gate>({ kind: 'loading' });

  const refresh = useCallback(async () => {
    setGate({ kind: 'loading' });
    const s = await auth.getSession();
    if (!s) return setGate({ kind: 'login' });
    if (s.aal !== 'aal2') return setGate({ kind: 'mfa', factorId: await auth.verifiedFactorId() });
    try {
      const me = await api.me();
      setGate(me.isAdmin ? { kind: 'ready', email: me.email } : { kind: 'not-admin', email: me.email });
    } catch {
      setGate({ kind: 'login' });
    }
  }, [auth, api]);

  useEffect(() => { void refresh(); }, [refresh]);

  const signOut = async () => { await auth.signOut(); void refresh(); };

  return (
    <div className="app console">
      <header className="brand"><div className="brand-name">SalonFlow Console</div><div className="brand-sub">สำหรับผู้ดูแลระบบ</div></header>
      {gate.kind === 'loading' && <Loading />}
      {gate.kind === 'login' && <Login auth={auth} onDone={refresh} />}
      {gate.kind === 'mfa' && <Mfa auth={auth} factorId={gate.factorId} onDone={refresh} />}
      {gate.kind === 'not-admin' && (
        <main className="page"><Empty title="บัญชีนี้ไม่ใช่ผู้ดูแล SalonFlow" text={gate.email} action={{ label: 'ออกจากระบบ', onClick: () => void signOut() }} /></main>
      )}
      {gate.kind === 'ready' && (
        <>
          <nav className="filter-chips console-nav" aria-label="เมนูคอนโซล" style={{ padding: '12px 16px 0' }}>
            <NavLink to="/console" end className={({ isActive }) => (isActive ? 'active' : '')}>ร้านค้า</NavLink>
            <NavLink to="/console/errors" className={({ isActive }) => (isActive ? 'active' : '')}>ข้อผิดพลาด</NavLink>
            <button type="button" onClick={() => void signOut()}>ออกจากระบบ</button>
          </nav>
          <Routes>
            <Route index element={<Shops api={api} />} />
            <Route path="shops/new" element={<NewShop api={api} />} />
            <Route path="shops/:id" element={<ShopDetail api={api} />} />
            <Route path="shops/:id/menu" element={<ShopMenu api={api} />} />
            <Route path="errors" element={<Errors api={api} />} />
            <Route path="*" element={<Empty title="ไม่พบหน้านี้" />} />
          </Routes>
        </>
      )}
    </div>
  );
}

function Login({ auth, onDone }: { auth: ConsoleAuth; onDone: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try { await auth.signIn(email.trim(), password); onDone(); } catch (err) { setError((err as Error).message); } finally { setBusy(false); }
  }
  return (
    <main className="page" style={{ paddingTop: 16 }}>
      {error && <Banner kind="error">{error}</Banner>}
      <form onSubmit={submit}>
        <Field label="อีเมล"><input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
        <Field label="รหัสผ่าน"><input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} /></Field>
        <Button type="submit" block loading={busy}>เข้าสู่ระบบ</Button>
      </form>
    </main>
  );
}

function Mfa({ auth, factorId, onDone }: { auth: ConsoleAuth; factorId: string | null; onDone: () => void }) {
  const [enrolled, setEnrolled] = useState<{ factorId: string; qrSvg: string; secret: string } | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!factorId) auth.enroll().then(setEnrolled).catch((e) => setError((e as Error).message));
  }, [auth, factorId]);
  const id = factorId ?? enrolled?.factorId ?? null;
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!id) return;
    setBusy(true);
    setError(null);
    try { await auth.verify(id, code.trim()); onDone(); } catch (err) { setError((err as Error).message); } finally { setBusy(false); }
  }
  return (
    <main className="page" style={{ paddingTop: 16 }}>
      {error && <Banner kind="error">{error}</Banner>}
      {!factorId && (
        <Card>
          <p style={{ marginBottom: 8 }}>ตั้งค่าครั้งแรก: เปิดแอป Google Authenticator หรือ Microsoft Authenticator แล้วสแกน QR นี้</p>
          {enrolled ? (
            <>
              <img src={enrolled.qrSvg} alt="QR code สำหรับแอป Authenticator" style={{ width: 200, height: 200, display: 'block', margin: '0 auto' }} />
              <p className="muted small" style={{ marginTop: 8 }}>หรือพิมพ์รหัสนี้ในแอป:</p>
              <p style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{enrolled.secret}</p>
            </>
          ) : <Loading />}
        </Card>
      )}
      <div style={{ height: 12 }} />
      <form onSubmit={submit}>
        <Field label="รหัส 6 หลักจากแอป Authenticator">
          <input inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} />
        </Field>
        <Button type="submit" block loading={busy} disabled={!id || code.length !== 6}>ยืนยัน</Button>
      </form>
    </main>
  );
}

const STATUS: Record<string, { label: string; tone: 'ok' | 'accent' | 'muted' | 'dark' | 'default' }> = {
  trial: { label: 'ทดลองใช้', tone: 'default' },
  active: { label: 'ใช้งาน', tone: 'ok' },
  overdue: { label: 'ค้างชำระ', tone: 'accent' },
  suspended: { label: 'ระงับ', tone: 'accent' },
  closed: { label: 'ปิดแล้ว', tone: 'muted' },
};
const DOT: Record<string, string> = { green: '🟢', yellow: '🟡', red: '🔴' };

function Shops({ api }: { api: ConsoleApi }) {
  const [rows, setRows] = useState<ConsoleShop[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { api.shops().then(setRows).catch((e) => setError(message(e))); }, [api]);
  return (
    <main className="page">
      <header className="page-head"><h1>ร้านค้า</h1><div className="page-actions"><Link className="btn btn-primary btn-sm" to="/console/shops/new">เพิ่มร้าน</Link></div></header>
      {error && <Banner kind="error">{error}</Banner>}
      {!rows && !error ? <Loading /> : rows && rows.length === 0 ? <Empty title="ยังไม่มีร้าน" /> : (
        <div className="list">
          {rows?.map((s) => (
            <Link key={s.id} to={`/console/shops/${s.id}`} className="card card-button" style={{ textDecoration: 'none' }}>
              <div className="row-between">
                <span className="card-title">{DOT[s.health.level ?? ''] ?? '⚪️'} {s.name}</span>
                <Chip tone={STATUS[s.status].tone}>{STATUS[s.status].label}</Chip>
              </div>
              <div className="muted small">/{s.slug} · ใช้ได้ถึง {s.serviceUntil} · เดือนนี้ {s.bookingsThisMonth} จอง · {s.messagesThisMonth} ข้อความ{s.lineConnected ? '' : ' · ยังไม่เชื่อม LINE'}</div>
            </Link>
          ))}
        </div>
      )}
    </main>
  );
}

function NewShop({ api }: { api: ConsoleApi }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', slug: '', phone: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setErrors({});
    setBusy(true);
    try {
      const shop = await api.createShop({ name: form.name, slug: form.slug, phone: form.phone.trim() || undefined });
      navigate(`/console/shops/${shop.id}`);
    } catch (err) {
      if (err instanceof ApiError && err.field) setErrors({ [err.field]: err.field === 'slug' ? 'ใช้ a-z 0-9 และ - ยาว 3–30 ตัว และต้องไม่ซ้ำร้านอื่น' : 'ข้อมูลไม่ถูกต้อง' });
      else setErrors({ form: message(err) });
    } finally {
      setBusy(false);
    }
  }
  return (
    <main className="page">
      <header className="page-head"><h1>เพิ่มร้าน</h1></header>
      {errors.form && <Banner kind="error">{errors.form}</Banner>}
      <form onSubmit={submit}>
        <Field label="ชื่อร้าน" error={errors.name}><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
        <Field label="ชื่อลิงก์ (ภาษาอังกฤษ)" error={errors.slug} hint="เช่น cool-cuts → ลิงก์จอง /s/cool-cuts เปลี่ยนภายหลังไม่ได้">
          <input value={form.slug} autoCapitalize="none" onChange={(e) => setForm({ ...form, slug: e.target.value })} />
        </Field>
        <Field label="เบอร์โทรร้าน (ถ้ามี)" error={errors.phone}><input value={form.phone} inputMode="tel" onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
        <Button type="submit" block loading={busy}>สร้างร้าน</Button>
      </form>
    </main>
  );
}

function Copy({ label, value }: { label: string; value: string }) {
  const toast = useToast();
  return (
    <div style={{ marginBottom: 10 }}>
      <p className="small muted">{label}</p>
      <div className="row">
        <code style={{ flex: 1, wordBreak: 'break-all', fontSize: '0.8rem', background: 'var(--soft)', padding: 8, borderRadius: 8 }}>{value}</code>
        <Button variant="secondary" className="btn-sm" onClick={() => { void navigator.clipboard?.writeText(value); toast('คัดลอกแล้ว', 'success'); }}>คัดลอก</Button>
      </div>
    </div>
  );
}

/** The salon's LINE menu designer, for setting a salon up without the owner's phone. */
function ShopMenu({ api }: { api: ConsoleApi }) {
  const { id = '' } = useParams();
  const [shop, setShop] = useState<ConsoleShopDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { api.shop(id).then(setShop, (e: unknown) => setError(message(e))); }, [api, id]);
  if (error) return <main className="page"><Banner kind="error">{error}</Banner></main>;
  if (!shop) return <Loading />;
  return (
    <main className="page">
      <header className="page-head">
        <Link className="icon-btn" to={`/console/shops/${shop.id}`} aria-label="กลับ">‹</Link>
        <h1>เมนู LINE · {shop.name}</h1>
      </header>
      {!shop.lineConnected && <Banner kind="warn">ร้านนี้ยังไม่เชื่อม LINE ใส่คีย์และกดตรวจสอบการเชื่อมต่อก่อน</Banner>}
      <MenuDesigner storeKey={`menu-art:${shop.slug}`} shopName={shop.name}
        upload={(b64, type, extras) => api.richMenuImage(shop.id, b64, type, extras)} />
    </main>
  );
}

const CHECK_LABEL: Record<string, string> = {
  token: 'Access token', webhook: 'Webhook URL', webhook_test: 'ทดสอบ Webhook', rich_menus: 'เมนู LINE', test_message: 'ข้อความทดสอบถึงเจ้าของร้าน',
};

function ShopDetail({ api }: { api: ConsoleApi }) {
  const { id = '' } = useParams();
  const toast = useToast();
  const [shop, setShop] = useState<ConsoleShopDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [line, setLine] = useState({ channelId: '', channelSecret: '', accessToken: '', loginChannelId: '', liffId: '' });
  const [lineErrors, setLineErrors] = useState<Record<string, string>>({});
  const [checks, setChecks] = useState<CheckItem[] | null>(null);
  const [invite, setInvite] = useState<string | null>(null);
  const [pay, setPay] = useState({ amount: '', months: 1, note: '' });
  const [confirmPay, setConfirmPay] = useState(false);
  const [note, setNote] = useState('');
  const [closeWord, setCloseWord] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const s = await api.shop(id);
      setShop(s);
      setNote(s.platformNote ?? '');
      setLine((l) => ({ ...l, channelId: l.channelId || (s.line.channelId ?? ''), loginChannelId: l.loginChannelId || (s.line.loginChannelId ?? ''), liffId: l.liffId || (s.line.liffId ?? '') }));
    } catch (e) {
      setError(message(e));
    }
  }, [api, id]);
  useEffect(() => { void load(); }, [load]);

  async function run(name: string, fn: () => Promise<void>) {
    setBusy(name);
    try { await fn(); } catch (e) { toast(message(e), 'error'); } finally { setBusy(null); }
  }

  if (error) return <main className="page"><Banner kind="error">{error}</Banner></main>;
  if (!shop) return <Loading />;

  const saveLine = () => run('line', async () => {
    setLineErrors({});
    try {
      await api.setLine(shop.id, line);
      toast('บันทึกคีย์ LINE แล้ว', 'success');
      setLine((l) => ({ ...l, channelSecret: '', accessToken: '' }));
      await load();
    } catch (e) {
      if (e instanceof ApiError && e.field) { setLineErrors({ [e.field]: 'ค่าไม่ถูกต้อง ตรวจสอบว่าคัดลอกครบ' }); return; }
      throw e;
    }
  });
  const field = (key: keyof typeof line, label: string, hint?: string, secret = false) => (
    <Field label={label} error={lineErrors[key]} hint={hint}>
      {key === 'accessToken'
        ? <textarea value={line[key]} onChange={(e) => setLine({ ...line, [key]: e.target.value })} rows={3} placeholder={shop.line.accessTokenHint ?? ''} />
        : <input type={secret ? 'password' : 'text'} autoComplete="off" value={line[key]} onChange={(e) => setLine({ ...line, [key]: e.target.value })}
          placeholder={key === 'channelSecret' ? shop.line.channelSecretHint ?? '' : ''} />}
    </Field>
  );
  const st = STATUS[shop.status];

  return (
    <main className="page">
      <header className="page-head"><h1>{shop.name}</h1><Chip tone={st.tone}>{st.label}</Chip></header>
      <Card>
        <p className="small">ลิงก์: /s/{shop.slug} · ใช้ได้ถึง <b>{shop.serviceUntil}</b>{shop.manualSuspend ? ' · ระงับโดยผู้ดูแล' : ''}</p>
        <p className="small">เดือนนี้ {shop.bookingsThisMonth} จอง · {shop.messagesThisMonth} ข้อความ LINE</p>
        <p className="small">สุขภาพ: {DOT[shop.health.level ?? ''] ?? '⚪️ ยังไม่ตรวจ'} {(shop.health.warnings ?? []).join(', ')}</p>
      </Card>

      <p className="section-title">1. ค่าที่ต้องใส่ใน LINE Developers</p>
      <Copy label="Webhook URL (Messaging API channel)" value={shop.webhookUrl} />
      <Copy label="LIFF Endpoint URL (LINE Login channel → LIFF)" value={shop.liffEndpoint} />

      <p className="section-title">2. คีย์ LINE ของร้าน</p>
      <Card>
        {field('channelId', 'Channel ID (Messaging API)')}
        {field('channelSecret', 'Channel secret', shop.line.channelSecretHint ? `บันทึกไว้แล้ว ${shop.line.channelSecretHint} (เว้นว่างไม่ได้เมื่อบันทึกใหม่)` : undefined, true)}
        {field('accessToken', 'Channel access token (long-lived)')}
        {field('loginChannelId', 'LINE Login channel ID')}
        {field('liffId', 'LIFF ID', 'เช่น 1650000000-AbCdEfGh')}
        <Button block loading={busy === 'line'} onClick={() => void saveLine()}>บันทึกคีย์ LINE</Button>
      </Card>

      <p className="section-title">3. ตรวจสอบและตั้งค่าอัตโนมัติ</p>
      <Button block variant="secondary" loading={busy === 'check'} onClick={() => void run('check', async () => { setChecks(await api.check(shop.id)); await load(); })}>
        ตรวจสอบการเชื่อมต่อ
      </Button>
      {checks && (
        <ul className="list" style={{ listStyle: 'none', padding: 0, marginTop: 10 }}>
          {checks.map((c) => (
            <li key={c.key} className="card small" style={{ borderColor: c.ok ? 'var(--line)' : 'var(--danger)' }}>
              {c.ok ? '✓' : '✗'} {c.key} · {CHECK_LABEL[c.key] ?? c.key}{c.detail ? ` — ${c.detail}` : ''}
            </li>
          ))}
        </ul>
      )}

      {shop.lineConnected && (
        <Link className="btn btn-secondary btn-block" style={{ marginTop: 10 }} to={`/console/shops/${shop.id}/menu`}>
          ตกแต่งเมนู LINE (โลโก้ · แบนเนอร์ · สี)
        </Link>
      )}

      <p className="section-title">4. ลิงก์เชิญเจ้าของร้าน</p>
      {invite ? <Copy label="ส่งลิงก์นี้ให้เจ้าของร้านเปิดใน LINE (ใช้ได้ 48 ชม. ครั้งเดียว)" value={invite} /> : (
        <Button block variant="secondary" loading={busy === 'invite'} onClick={() => void run('invite', async () => setInvite((await api.ownerInvite(shop.id)).url))}>
          สร้างลิงก์เชิญเจ้าของร้าน
        </Button>
      )}

      <p className="section-title">5. การชำระเงิน</p>
      <Card>
        <div className="row">
          <Field label="จำนวนเงิน (บาท)"><input type="number" inputMode="numeric" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} /></Field>
          <Field label="จำนวนเดือน">
            <select value={pay.months} onChange={(e) => setPay({ ...pay, months: Number(e.target.value) })}>
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </Field>
        </div>
        <Field label="หมายเหตุ (ถ้ามี)"><input value={pay.note} onChange={(e) => setPay({ ...pay, note: e.target.value })} /></Field>
        <Button block disabled={!/^\d+$/.test(pay.amount)} onClick={() => setConfirmPay(true)}>บันทึกการชำระ</Button>
      </Card>
      {shop.payments.length > 0 && (
        <table className="simple" style={{ marginTop: 10 }}>
          <thead><tr><th>วันที่</th><th className="num">บาท</th><th className="num">เดือน</th><th>โดย</th></tr></thead>
          <tbody>{shop.payments.map((p) => <tr key={p.paidAt}><td>{p.paidAt.slice(0, 10)}</td><td className="num">{p.amount}</td><td className="num">{p.months}</td><td>{p.recordedBy}</td></tr>)}</tbody>
        </table>
      )}
      <Dialog open={confirmPay} title="บันทึกการชำระ?" loading={busy === 'pay'} onCancel={() => setConfirmPay(false)}
        onConfirm={() => void run('pay', async () => {
          const r = await api.recordPayment(shop.id, { amount: Number(pay.amount), months: pay.months, note: pay.note.trim() || undefined });
          setConfirmPay(false);
          setPay({ amount: '', months: 1, note: '' });
          toast(`ใช้ได้ถึง ${r.serviceUntil}`, 'success');
          await load();
        })}>
        <p>{pay.amount} บาท · {pay.months} เดือน</p>
      </Dialog>

      <p className="section-title">6. สถานะและบันทึก</p>
      <Field label="บันทึกภายใน (เห็นเฉพาะผู้ดูแล)"><textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} /></Field>
      <div className="stack">
        <Button variant="secondary" loading={busy === 'note'} onClick={() => void run('note', async () => { setShop(await api.updateShop(shop.id, { platformNote: note })); toast('บันทึกแล้ว', 'success'); })}>บันทึกโน้ต</Button>
        {shop.status !== 'closed' && (
          <Button variant="secondary" loading={busy === 'suspend'} onClick={() => void run('suspend', async () => setShop(await api.updateShop(shop.id, { manualSuspend: !shop.manualSuspend })))}>
            {shop.manualSuspend ? 'ยกเลิกการระงับ' : 'ระงับร้าน (หยุดรับจองใหม่)'}
          </Button>
        )}
        {shop.status === 'closed' ? (
          <Button variant="secondary" loading={busy === 'reopen'} onClick={() => void run('reopen', async () => setShop(await api.updateShop(shop.id, { reopen: true })))}>เปิดร้านกลับมา</Button>
        ) : (
          <Card>
            <p className="small">ปิดร้านถาวร: บอทหยุดตอบ ลูกค้าจองไม่ได้ ข้อมูลถูกลบหลัง 90 วัน</p>
            <Field label={`พิมพ์ ${shop.slug} เพื่อยืนยัน`}><input value={closeWord} onChange={(e) => setCloseWord(e.target.value)} /></Field>
            <Button variant="danger" disabled={closeWord !== shop.slug} loading={busy === 'close'} onClick={() => void run('close', async () => setShop(await api.updateShop(shop.id, { close: true })))}>ปิดร้าน</Button>
          </Card>
        )}
      </div>

      {shop.recentErrors.length > 0 && (
        <>
          <p className="section-title">ข้อผิดพลาดล่าสุด</p>
          <div className="list">{shop.recentErrors.map((e, i) => <div key={i} className="card small">{e.createdAt.slice(0, 16)} · {e.source} · {e.message}</div>)}</div>
        </>
      )}
    </main>
  );
}

function Errors({ api }: { api: ConsoleApi }) {
  const [rows, setRows] = useState<ErrorRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { api.errors().then(setRows).catch((e) => setError(message(e))); }, [api]);
  return (
    <main className="page">
      <header className="page-head"><h1>ข้อผิดพลาด</h1></header>
      {error && <Banner kind="error">{error}</Banner>}
      {!rows && !error ? <Loading /> : rows?.length === 0 ? <Empty title="ไม่มีข้อผิดพลาด" /> : (
        <div className="list">{rows?.map((e) => <div key={e.id} className="card small">{e.createdAt.slice(0, 16)} · {e.shopSlug ?? 'ระบบ'} · {e.source} · {e.message}</div>)}</div>
      )}
    </main>
  );
}
